import { useTranslation } from 'react-i18next'
import type { ProcessingMode } from '@/contexts/CaptureContext'

interface MoriChoiceGateProps {
  /** True when the capture has audio (voice_moment / general_memory with voice). */
  hasAudio: boolean
  onChoose: (mode: ProcessingMode) => void
}

export default function MoriChoiceGate({ hasAudio, onChoose }: MoriChoiceGateProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4 w-full px-4 py-2">
      <p className="text-[12px] font-semibold text-muted-foreground uppercase tracking-widest text-center">
        {t('capture.choiceGate.title')}
      </p>

      {/* ── AI path ── */}
      <button
        type="button"
        onClick={() => onChoose('full')}
        aria-label={t('capture.choiceGate.aiButton.ariaLabel')}
        className="relative flex flex-col items-center gap-3 w-full rounded-2xl border-2 border-primary/30 bg-primary/5 px-4 py-6 transition-all active:scale-[0.98] hover:border-primary/60 hover:bg-primary/10"
      >
        <span
          className="text-4xl animate-bounce select-none"
          aria-hidden
          style={{ animationDuration: '1.4s', animationTimingFunction: 'cubic-bezier(0.28, 0.84, 0.42, 1)' }}
        >
          🌰
        </span>
        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-[16px] font-bold text-foreground">
            {t('capture.choiceGate.aiButton.title')}
          </p>
          <p className="text-[13px] text-muted-foreground leading-snug max-w-[280px]">
            {hasAudio
              ? t('capture.choiceGate.aiButton.subtitleAudio')
              : t('capture.choiceGate.aiButton.subtitleText')}
          </p>
        </div>
      </button>

      {/* ── Nap path ── */}
      <button
        type="button"
        onClick={() => onChoose(hasAudio ? 'transcript_only' : 'skip')}
        aria-label={t('capture.choiceGate.napButton.ariaLabel')}
        className="flex flex-col items-center gap-3 w-full rounded-2xl border border-border bg-muted/40 px-4 py-6 transition-all active:scale-[0.98] hover:bg-muted/70"
      >
        <span
          className="text-4xl animate-pulse select-none"
          aria-hidden
          style={{ animationDuration: '3s' }}
        >
          🐿️
        </span>
        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-[15px] font-semibold text-foreground">
            {t('capture.choiceGate.napButton.title')}
          </p>
          <p className="text-[13px] text-muted-foreground leading-snug max-w-[280px]">
            {t('capture.choiceGate.napButton.subtitle')}
          </p>
        </div>
      </button>
    </div>
  )
}
